import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CreateXperiencePage from './pages/CreateXperiencePage';
import EventsPage from './pages/EventsPage';
import XperienceDetailPage from './pages/XperienceDetailPage';
import XperienceMediaDetailPage from './pages/XperienceMediaDetailPage';
import ProfilePage from './pages/ProfilePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import BeACreatorPage from './pages/BeACreatorPage';
import ModerationPage from './pages/Moderation/ModerationPage';
import AdminXperiencesPage from './pages/Moderation/AdminXperiencesPage';
import AdminCreatorsPage from './pages/AdminCreatorsPage';
import AdminCreatorDetailPage from './pages/AdminCreatorDetailPage';
import { t } from './i18n';

const PrivateRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { creator, loading } = useAuth();

  if (loading) {
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      color: '#a0a0a0'
    }}>{t('common.loading')}</div>;
  }

  return creator ? children : <Navigate to="/login" />;
};

const AdministratorRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { creator, loading } = useAuth();

  if (loading) {
    return <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      color: '#a0a0a0'
    }}>{t('common.loading')}</div>;
  }

  if (!creator) {
    return <Navigate to="/login" />;
  }

  return creator.role === 'administrator' ? children : <Navigate to="/" />;
};

const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { creator, loading } = useAuth();

  if (loading) {
    return <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      color: '#a0a0a0'
    }}>{t('common.loading')}</div>;
  }

  return creator ? <Navigate to="/" /> : children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/be-a-creator" element={
        <PublicRoute>
          <BeACreatorPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/" element={
        <PrivateRoute>
          <HomePage />
        </PrivateRoute>
      } />
      <Route path="/create-xperience" element={
        <PrivateRoute>
          <CreateXperiencePage />
        </PrivateRoute>
      } />
      <Route path="/events" element={
        <AdministratorRoute>
          <EventsPage />
        </AdministratorRoute>
      } />
      <Route path="/events/new" element={
        <AdministratorRoute>
          <EventsPage />
        </AdministratorRoute>
      } />
      <Route path="/events/:eventId/edit" element={
        <AdministratorRoute>
          <EventsPage />
        </AdministratorRoute>
      } />
      <Route path="/xperience/:id" element={
        <PrivateRoute>
          <XperienceDetailPage />
        </PrivateRoute>
      } />
      <Route path="/xperience-media/new/:xperienceId" element={ <PrivateRoute><XperienceMediaDetailPage /></PrivateRoute> } />
      <Route path="/xperience-media/:id" element={
        <PrivateRoute>
          <XperienceMediaDetailPage />
        </PrivateRoute>
      } />
      <Route path="/profile" element={
        <PrivateRoute>
          <ProfilePage />
        </PrivateRoute>
      } />
      <Route path="/moderation" element={
        <PrivateRoute>
          <ModerationPage />
        </PrivateRoute>
      } />
      <Route path="/admin/xperiences" element={
        <PrivateRoute>
          <AdminXperiencesPage />
        </PrivateRoute>
      } />
      <Route path="/admin/creators" element={
        <PrivateRoute>
          <AdminCreatorsPage />
        </PrivateRoute>
      } />
      <Route path="/admin/creators/:id" element={
        <PrivateRoute>
          <AdminCreatorDetailPage />
        </PrivateRoute>
      } />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;
